import axios from "axios";
import { API_BASE_URL } from "../../config";
import { parsePhones } from "../utils/phones";
import { authHeaders } from "../utils/session";

const getAuthHeaders = () => ({
  ...authHeaders(),
});

// ==================== ENQUIRY LIST SERVICES ====================

/**
 * Fetch paginated enquiry list with filters
 */
export const fetchEnquiryList = async (page = 1, filterData = {}) => {
  try {
    /* Scoping is enforced by the API from the token — a user only ever receives
       the leads their role and reporting line allow. `assign_to` is passed on
       only when the user picks someone in the filter, i.e. to NARROW the view,
       never as the security boundary. */
    const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
      params: { page, limit: 10, ...filterData },
      headers: getAuthHeaders(),
    });
    const payload = res?.data;
    if (payload?.status !== 200) throw new Error(payload?.message || "Failed to fetch enquiry list");
    return {
      data: Array.isArray(payload?.data) ? payload.data : [],
      total: Number(payload?.total_records ?? 0),
      pagination: payload?.pagination ?? {},
      totalPages: payload?.pagination?.total_pages ?? 1,
    };
  } catch (err) {
    throw new Error(err?.response?.data?.message || err?.message || "Failed to fetch enquiry list");
  }
};


// Fetch lead-details/trademark-enquiries details
export const fetchLeadDetails = async (id) => {
  const response = await axios.get(
    `${API_BASE_URL}/trademark-enquiries-details/${id}`,
    { headers: getAuthHeaders() }
  );
   if(response.data.status === 200) return response.data;
  throw new Error(response?.data?.message || "Failed to fetch lead/trademark details");
};


/**
 * Fetch single enquiry details by id
 */
export const fetchEnquiryDetails = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/trademark-enquiries-details/${id}`, {
    headers: getAuthHeaders(),
  });
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to fetch enquiry details");
};

/**
 * Fetch internal user list with lead_limit info.
 * Used by both the Allocate modal and the "Assigned Admin" filter dropdown.
 *
 * Endpoint: GET /get-internal-user-list?page=1&limit=100
 */
export const fetchInternalUserList = async (page = 1, limit = 10, { all = false } = {}) => {
  try {
    // all=1 returns every internal user unpaginated. The team tree is built from
    // reporting_to_user_id, so a truncated list would silently drop a manager's
    // reportees once the org grows past one page.
    const response = await axios.get(`${API_BASE_URL}/get-internal-user-list`, {
      params: all ? { all: 1 } : { page, limit },
      headers: getAuthHeaders(),
    });
    if (response.data.status === 200) {
      const { data, total, pagination } = response.data;
      return {
        data: Array.isArray(data) ? data : [],
        total: total ?? 0,
        totalPages: pagination?.total_pages ?? 1,
      };
    }
    return { data: [], total: 0, totalPages: 1 };
  } catch {
    return { data: [], total: 0, totalPages: 1 };
  }
};

/**
 * Fetch enquiries for XLSX export (high limit, no pagination display)
 */
export const fetchExportEnquiries = async (filterData = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
      params: { ...filterData, is_positive: "", page: 1, limit: 500 },
      headers: getAuthHeaders(),
    });
    const payload = res?.data;
    if (payload?.status !== 200) throw new Error(payload?.message || "Failed to fetch export data");
    return Array.isArray(payload?.data) ? payload.data : [];
  } catch (err) {
    throw new Error(err?.response?.data?.message || err?.message || "Failed to fetch export data");
  }
};

// ==================== ENQUIRY MUTATION SERVICES ====================

/**
 * Delete one or multiple enquiries
 * @param {number|number[]} ids
 */
export const deleteEnquiry = async (ids) => {
  const idArray = Array.isArray(ids) ? ids : [ids];
  const response = await axios.post(
    `${API_BASE_URL}/delete-my-trademark-enquiry`,
    JSON.stringify({ id: idArray }),
    { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
  );
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to delete enquiry");
};

/**
 * Update lead contact details (name, email, phone, additional_phone)
 */
export const updateLeadContact = async (id, { name, email, phone, additional_phone }) => {
  const response = await axios.post(
    `${API_BASE_URL}/trademark-enquiries-update/${id}`,
    {
      name,
      email,
      phone,
      // The endpoint takes any number of alternates: an array or a CSV string.
      // Sending the array keeps single-number and multi-number saves identical.
      additional_phone: parsePhones(additional_phone),
    },
    { headers: getAuthHeaders() }
  );
  if (response.data.status) return response.data;
  throw new Error(response.data.message || "Failed to update contact");
};

/**
 * Update enquiry status (positive / negative)
 * status: 1 = Positive, 2 = Negative
 */
export const updateEnquiryStatus = async ({ id, status, remarks = "" }) => {
  const response = await axios.post(
    `${API_BASE_URL}/is-positive-enquiry`,
    { id, status, remarks },
    { headers: getAuthHeaders() }
  );
  if (response.data.status === 200) return response.data;
  throw new Error(response.data.message || "Failed to update enquiry status");
};

/**
 * Import enquiries from an Excel file
 */
export const importEnquiries = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  let response;
  try {
    response = await axios.post(
      `${API_BASE_URL}/import-trademark-enquiry`,
      formData,
      { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
    );
  } catch (err) {
    /* The importer answers with four different shapes; axios only ever gives us
       "Request failed with status code NNN", so everything useful lives in the
       body and has to be pulled out here.

         422 + { error, rows: [...] }  → row-wise validation, nothing imported
         422 + { error }               → file missing / no data rows
         500 + { error }               → rolled back server-side
    */
    const data = err?.response?.data || {};
    const status = err?.response?.status;
    const failure = new Error(
      data.error ||
        data.message ||
        (status >= 500
          ? "Import failed, please try again."
          : "Failed to import enquiries"),
    );
    // Row-level reasons the user must fix before re-uploading the whole file.
    failure.rows = Array.isArray(data.rows)
      ? data.rows
      : Array.isArray(data.errors?.excel)
        ? data.errors.excel
        : null;
    failure.isServerError = status >= 500;
    throw failure;
  }

  const data = response.data || {};

  // It can also answer HTTP 200 with the failure in the body.
  if (data.error || data.status === false || data.status === 400 || data.status === 422) {
    const failure = new Error(data.error || data.message || "Failed to import enquiries");
    failure.rows = Array.isArray(data.rows) ? data.rows : null;
    throw failure;
  }

  return {
    message: data.message || "File imported successfully",
    imported: Number(data.imported ?? 0),
    skippedCount: Number(data.skipped_count ?? (data.skipped || []).length ?? 0),
    skipped: Array.isArray(data.skipped) ? data.skipped : [],
  };
};

/**
 * Allocate selected leads to an internal user.
 *
 * Endpoint : POST /manual-asign-lead-to-user
 * Payload  : { internal_user_id: number, lead_ids: number[] }
 */
export const allocateLeads = async ({ internal_user_id, lead_ids }) => {
  let response;
  try {
    response = await axios.post(
      `${API_BASE_URL}/manual-asign-lead-to-user`,
      { internal_user_id, lead_ids },
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
    );
  } catch (err) {
    // The API puts the real reason in the body even on 404/422 — surface that
    // instead of axios's "Request failed with status code NNN".
    const data = err?.response?.data;
    throw new Error(
      data?.message || data?.error || err?.message || "Failed to allocate leads"
    );
  }
  /* This endpoint answers `status: true` (not 200) on success. Comparing against
     200 only made every successful assignment throw — with the API's own success
     text as the "error" message. */
  const data = response.data || {};
  if (data.status === 200 || data.status === true || data.success) return data;
  throw new Error(data.message || data.error || "Failed to allocate leads");
};
